import * as React from 'react';
import { useSelector } from '@/state/store';
import {
    stateMap,
    stateOmrader,
    statePreferences,
    stateSearchDropdownState,
    stateUser,
    stateVegnett,
} from '@/selectors/selectors';
import { kartutsnittFromWktStrings, wktToGeometry } from '@/utils/SpatialHelper';
import urlJoin from 'url-join';
import { memoize } from 'lodash-es';
import { VegobjekttypeCategoryState, VegobjekttypeState } from '@/state';
import { download } from '@/utils/Utils';
import { ConfigAndData, ConfigAndDataContext } from '@/state/context/ConfigAndDataContext';
import { useContext } from 'react';
import { getBlob } from '@/server/server';

export const useVegnettUrls = (restapi_url: string, eksport_url: string) => {
    const areaState = useSelector(stateOmrader);
    const areaQueries = areaState.toQueries();
    const mapQueries = useSelector(stateMap).toQueries();
    const roadNetQueries = useSelector(stateVegnett).toQueries();
    const timestamp = useSelector(stateSearchDropdownState).timestamp;
    const preferences = useSelector(statePreferences);
    const params = {
        ...mapQueries.toParams(),
        ...areaQueries.toParams(),
        ...roadNetQueries.withTidspunkt(timestamp).toParams(),
        ...preferences.toExportQueries().toParams(),
    };
    const base = `/vegnett/veglenkesekvenser/segmentert`;
    const hasOmradeWithGeometry =
        !!params.fylke || !!params.kommune || !!params.kontraktsomrade || !!params.riksvegrute;
    const query =
        '?' +
        Object.keys(params)
            .filter(key => !hasOmradeWithGeometry || (hasOmradeWithGeometry && key !== 'kartutsnitt'))
            .map(key => `${key}=${encodeURIComponent(params[key])}`)
            .join('&');

    const apiParam = `&api=${encodeURIComponent(restapi_url)}`;
    const charsetParam = '&tegnsett=utf-8';
    const sosiutsnittParam = !query.includes('kartutsnitt')
        ? '&sosiutsnitt=' +
          kartutsnittFromWktStrings(
              areaState
                  .withVegsystemreferanser([])
                  .getFullList()
                  .map(omrade => omrade?.vegobjekt?.geometri?.wkt)
                  .filter(e => !!e)
          ).toQueryString()
        : '';
    return {
        json: urlJoin(restapi_url, base, query),
        sosi: urlJoin(eksport_url, base + '.sos', query + charsetParam + apiParam + sosiutsnittParam),
        csv: urlJoin(eksport_url, base + '.csv', query + apiParam),
    };
};

export const isSensitive = memoize((typeId, context) => {
    const type = context.data.datakatalog.byId[typeId];
    return type.sensitiv || type.egenskapstyper.some(e => e.sensitivitet > 0);
});

export const useIsSensitiveAvailable = (typeId: number): boolean => {
    const context = useContext(ConfigAndDataContext);
    const userState = useSelector(stateUser);
    return userState.isLoggedIn && isSensitive(typeId, context);
};

export const useUrlsParams = (
    votState: VegobjekttypeState,
    catState?: VegobjekttypeCategoryState
): string => {
    const mapState = useSelector(stateMap);
    const omraderState = useSelector(stateOmrader);
    const vegnettState = useSelector(stateVegnett);
    const searchDropdownState = useSelector(stateSearchDropdownState);
    const preferences = useSelector(statePreferences);

    const getParams = () => {
        const votQueries = !catState
            ? votState.toQueries().withTidspunkt(searchDropdownState.timestamp)
            : votState
                  .toQueries()
                  .withTidspunkt(searchDropdownState.timestamp)
                  .withKategorifilter(catState.filters);
        return {
            ...votQueries.toParams(),
            ...mapState.toQueries().toParams(),
            ...omraderState.toQueries().toParams(),
            ...vegnettState.toQueries().toParams(),
            ...preferences.toExportQueries().toParams(),
        };
    };

    const getAreaParams = () => {
        const geometries = omraderState
            .getGeometryFetchables()
            .map(item => item?.vegobjekt?.geometri?.wkt)
            .filter(wkt => !!wkt)
            .map(wkt => wktToGeometry(wkt));

        let minX = Infinity,
            minY = Infinity,
            maxX = -Infinity,
            maxY = -Infinity;

        geometries.forEach(geometry => {
            const extent = geometry.getExtent();
            minX = Math.min(minX, extent[0]);
            minY = Math.min(minY, extent[1]);
            maxX = Math.max(maxX, extent[2]);
            maxY = Math.max(maxY, extent[3]);
        });

        return minX + ',' + minY + ',' + maxX + ',' + maxY;
    };

    const params = getParams();

    if (!!params.fylke || !!params.kommune || !!params.kontraktsomrade || !!params.riksvegrute) {
        params.kartutsnitt = getAreaParams();
    }
    return Object.keys(params)
        .reduce((prev: string[], key: string) => prev.concat(`${key}=${encodeURIComponent(params[key])}`), [])
        .concat(`inkluder=${encodeURIComponent('alle')}`)
        .join('&');
};

export const handleDownload = async (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
    isSensitiveAvailable: boolean,
    downloadUrl: string,
    context: ConfigAndData,
    dispatch: any,
    setDownloading: (downloading: boolean) => void
) => {
    e.preventDefault();
    setDownloading(true);

    const match = downloadUrl.match(/vegobjekter\/(\d+)\.(sos|csv)/);
    const vegobjektId = match ? parseInt(match[1]) : undefined;
    const roadObjectTypeName =
        context.data.datakatalog.vegobjekttyper.find(type => type.id === vegobjektId)?.sosinvdbnavn ??
        'vegobjekt';
    const sensitivityLabel = isSensitiveAvailable ? '-sensitiv' : '';
    const fileType = match ? match[2] : 'sos';

    const fileName = `${roadObjectTypeName}${sensitivityLabel}-eksport.${fileType}`;
    const blob = await getBlob(downloadUrl, isSensitiveAvailable);
    const objectURL = URL.createObjectURL(blob);

    download(objectURL, fileName);

    URL.revokeObjectURL(objectURL);
    setDownloading(false);
};
